'use client'

import { useLocale } from '@/components/LocaleContext'
import Link from 'next/link'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  getNewArrivalsEffectiveTotal,
  NEW_ARRIVALS_MAX_TOTAL,
  NEW_ARRIVALS_PAGE_SIZE,
  type NewArrivalProduct,
} from '@/lib/newArrivals'
import { collectAmbiguousPartNumbers, getProductDetailSlug } from '@/lib/productUrls'
import { ZoomableImage } from '../../../components/frontend/ZoomableImage'

const MORE_LABELS: Record<AppLocale, string> = {
  en: 'More',
  fr: 'Plus',
  ar: 'المزيد',
}

type NewArrivalsResponse = {
  products: NewArrivalProduct[]
  limit: number
}

type NewArrivalsSectionProps = {
  products: NewArrivalProduct[]
  totalDocs: number
  heading?: string
  pageSize?: number
  maxTotal?: number
}

function getNewArrivalProductHref(
  product: NewArrivalProduct | undefined,
  ambiguousPartNumbers: Set<string>,
): string {
  if (!product?.id) return '/products'
  return `/products/${getProductDetailSlug(
    { id: product.id, partNumber: product.partNumber, sku: product.sku },
    { ambiguousPartNumbers },
  )}`
}

export function NewArrivalsSection({
  products: initialProducts,
  totalDocs: initialTotalDocs,
  heading = 'New Arrivals',
  pageSize = NEW_ARRIVALS_PAGE_SIZE,
  maxTotal = NEW_ARRIVALS_MAX_TOTAL,
}: NewArrivalsSectionProps) {
  const { locale, translatedData } = useLocale()
  const initialEffectiveTotal = Math.min(getNewArrivalsEffectiveTotal(initialTotalDocs), maxTotal)
  const [products, setProducts] = useState<NewArrivalProduct[]>(
    initialProducts.slice(0, Math.min(pageSize, initialEffectiveTotal)),
  )
  const totalDocs = initialEffectiveTotal
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [canLoadMore, setCanLoadMore] = useState(
    initialProducts.slice(0, Math.min(pageSize, initialEffectiveTotal)).length <
      initialEffectiveTotal,
  )

  const effectiveTotal = Math.min(getNewArrivalsEffectiveTotal(totalDocs), maxTotal)
  const showMoreButton =
    canLoadMore && products.length < maxTotal && products.length < effectiveTotal
  const prefetchCacheRef = useRef<{ key: string; data: NewArrivalsResponse } | null>(null)
  const [first, second, third, fourth, fifth, ...rest] = products
  const ambiguousPartNumbers = useMemo(
    () => collectAmbiguousPartNumbers(products),
    [products],
  )
  const getImageSrc = (url: string | null | undefined) => (url?.trim() ? url : '/no-image.webp')
  const getImageAlt = (alt: string | undefined) => (alt?.trim() ? alt : 'No image')
  const isRemoteImage = (url: string | null | undefined) =>
    Boolean(url?.trim() && url.startsWith('http'))

  const appendUniqueProducts = useCallback(
    (
      previousProducts: NewArrivalProduct[],
      nextProducts: NewArrivalProduct[],
      capTotal: number,
    ) => {
      const seen = new Set(
        previousProducts.map((product) => product.id ?? product.sku ?? product.name),
      )
      const deduplicatedNewProducts = nextProducts.filter((product) => {
        const key = product.id ?? product.sku ?? product.name
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
      })

      const mergedProducts = [...previousProducts, ...deduplicatedNewProducts].slice(0, capTotal)
      setProducts(mergedProducts)

      return {
        addedCount: deduplicatedNewProducts.length,
        nextLength: mergedProducts.length,
      }
    },
    [],
  )

  const buildFetchUrl = useCallback(() => {
    const remaining = Math.min(effectiveTotal, maxTotal) - products.length
    const requestLimit = Math.min(pageSize, remaining)

    if (requestLimit <= 0) return null

    const excludeIds = products
      .map((product) => product.id)
      .filter((id): id is string => Boolean(id))
      .join(',')

    return `/api/products/new-arrivals?limit=${requestLimit}&exclude=${encodeURIComponent(excludeIds)}`
  }, [effectiveTotal, maxTotal, pageSize, products])

  useEffect(() => {
    const url = buildFetchUrl()
    if (!url || !showMoreButton || isLoading) return

    if (prefetchCacheRef.current?.key === url) return

    const controller = new AbortController()

    void fetch(url, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: NewArrivalsResponse | null) => {
        if (data) {
          prefetchCacheRef.current = { key: url, data }
        }
      })
      .catch(() => {
        // Ignore prefetch aborts/errors; click handler performs the real fetch.
      })

    return () => controller.abort()
  }, [buildFetchUrl, isLoading, showMoreButton])

  const handleLoadMore = useCallback(async () => {
    if (isLoading || !showMoreButton) return

    const url = buildFetchUrl()
    if (!url) {
      setCanLoadMore(false)
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const cached = prefetchCacheRef.current?.key === url ? prefetchCacheRef.current.data : null
      const data =
        cached ??
        ((await fetch(url).then((response) => {
          if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`)
          }
          return response.json()
        })) as NewArrivalsResponse)

      prefetchCacheRef.current = null

      const { addedCount, nextLength } = appendUniqueProducts(
        products,
        Array.isArray(data.products) ? data.products : [],
        effectiveTotal,
      )

      if (addedCount === 0 || nextLength >= maxTotal || nextLength >= effectiveTotal) {
        setCanLoadMore(false)
      }
    } catch (error) {
      console.error('Failed to load more new arrivals:', error)
      setErrorMessage('Unable to load more products. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [
    appendUniqueProducts,
    buildFetchUrl,
    effectiveTotal,
    isLoading,
    maxTotal,
    products,
    showMoreButton,
  ])

  const moreButtonLabel = useMemo(() => {
    if (isLoading) return translatedData?.common?.loading ?? 'Loading...'
    return MORE_LABELS[locale] ?? 'More'
  }, [isLoading, locale, translatedData?.common?.loading])

  if (!first) return null

  return (
    <section
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
      className="w-full pt-12 md:pt-16"
      aria-labelledby="new-arrivals-heading"
    >
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2
          id="new-arrivals-heading"
          className="font-bebas-neue font-bold uppercase text-[49.54px] leading-[54.49px] tracking-[1px] text-[#111]"
        >
          {heading}
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-2 sm:gap-8 md:grid-cols-2 md:grid-rows-2">
          {/* Large left panel - first product */}
          <Link
            href={getNewArrivalProductHref(first, ambiguousPartNumbers)}
            className="group relative flex min-h-[500px] flex-col overflow-hidden rounded-md md:row-span-2"
          >
            <div className="absolute inset-0 h-full w-full overflow-hidden">
              <div className="relative h-full w-full">
                <ZoomableImage
                  src={getImageSrc(first.imageUrl)}
                  alt={getImageAlt(first.imageAlt)}
                  fill
                  className="object-contain object-center transition-transform duration-300 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  unoptimized={isRemoteImage(first.imageUrl)}
                  stopPropagation
                />
              </div>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-3 py-4 text-white">
                <h3 className="font-poppins text-[30px] font-bold uppercase tracking-wider leading-none">
                  {first?.partNumber}
                </h3>
                {/* <span className="mt-0 inline-flex items-center font-poppins text-[14px] font-medium underline underline-offset-2 group-hover:text-[#FFDFDF]">
                  {first.name}
                  <svg
                    className="ml-1 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span> */}
              </div>
            </div>
          </Link>

          {/* Top right panel - two small products */}
          {(second || third) && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-3">
              {[second, third].filter(Boolean).map((product) => (
                <Link
                  key={product?.id ?? product?.sku ?? product?.name}
                  href={getNewArrivalProductHref(product, ambiguousPartNumbers)}
                  className="group relative mx-auto -translate-x-[3px] flex h-[300px] w-[calc(100%-10px)] flex-col overflow-hidden rounded-md bg-transparent"
                >
                  <div className="absolute inset-0 h-full w-full overflow-hidden">
                    <div className="relative h-full w-full">
                      <ZoomableImage
                        src={getImageSrc(product?.imageUrl)}
                        alt={getImageAlt(product?.imageAlt)}
                        fill
                        className="object-contain object-center transition-transform duration-300 group-hover:scale-105"
                        sizes="(max-width: 768px) 50vw, 25vw"
                        unoptimized={isRemoteImage(product?.imageUrl)}
                        stopPropagation
                      />
                    </div>
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-2 py-4 text-white">
                      <h3 className="font-poppins text-[30px] font-bold uppercase tracking-wider leading-none sm:text-[30px]">
                        {product?.partNumber}
                      </h3>
                      {/* <span className="mt-0 inline-flex items-center font-poppins text-[14px] font-medium underline underline-offset-2 group-hover:text-[#FFDFDF] sm:text-[14px]">
                        {product?.name}
                        <svg
                          className="ml-1 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </span> */}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Bottom right panel - two small products */}
          {(fourth || fifth) && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-3">
              {[fourth, fifth].filter(Boolean).map((product) => (
                <Link
                  key={product?.id ?? product?.sku ?? product?.name}
                  href={getNewArrivalProductHref(product, ambiguousPartNumbers)}
                  className="group relative mx-auto -translate-x-[3px] flex h-[300px] w-[calc(100%-10px)] flex-col overflow-hidden rounded-md bg-transparent"
                >
                  <div className="absolute inset-0 h-full w-full overflow-hidden">
                    <div className="relative h-full w-full">
                      <ZoomableImage
                        src={getImageSrc(product?.imageUrl)}
                        alt={getImageAlt(product?.imageAlt)}
                        fill
                        className="object-contain object-center transition-transform duration-300 group-hover:scale-105"
                        sizes="(max-width: 768px) 50vw, 25vw"
                        unoptimized={isRemoteImage(product?.imageUrl)}
                        stopPropagation
                      />
                    </div>
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-2 py-4 text-white">
                      <h3 className="font-poppins text-[30px] font-bold uppercase tracking-wider leading-none sm:text-[30px]">
                        {product?.partNumber}
                      </h3>
                      {/* <span className="mt-0 inline-flex items-center font-poppins text-[14px] font-medium underline underline-offset-2 group-hover:text-[#FFDFDF] sm:text-[14px]">
                        {product?.name}
                        <svg
                          className="ml-1 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </span> */}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {rest?.map((product, index) => (
            <Link
              key={`${product.id ?? product.sku ?? product.name ?? 'new-arrival'}-${index}`}
              href={getNewArrivalProductHref(product, ambiguousPartNumbers)}
              className="group relative mx-auto -translate-x-[3px] flex h-[300px] w-[calc(100%-5px)] flex-col overflow-hidden rounded-md bg-transparent"
            >
              <div className="absolute inset-0 h-full w-full overflow-hidden">
                <div className="relative h-full w-full">
                  <ZoomableImage
                    src={getImageSrc(product.imageUrl)}
                    alt={getImageAlt(product.imageAlt)}
                    fill
                    className="object-contain object-center transition-transform duration-300 group-hover:scale-105"
                    sizes="(max-width: 768px) 100vw, 25vw"
                    unoptimized={isRemoteImage(product.imageUrl)}
                    stopPropagation
                  />
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-2 py-4 text-white">
                  <h3 className="font-poppins text-[30px] font-bold uppercase tracking-wider leading-none sm:text-[30px]">
                    {product.partNumber}
                  </h3>
                  {/* <span className="mt-0 inline-flex items-center font-poppins text-[14px] font-medium underline underline-offset-2 group-hover:text-[#FFDFDF] sm:text-[14px]">
                      {product.name}
                      <svg
                        className="ml-1 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </span> */}
                </div>
              </div>
            </Link>
          ))}
          {showMoreButton ? (
            <div
              key="new-arrivals-more"
              className="flex min-h-[280px] items-center justify-center rounded-md"
            >
              <button
                type="button"
                disabled={isLoading}
                onClick={handleLoadMore}
                className="font-bebas-neue text-[30px] font-bold leading-[54.49px] tracking-[1px] text-[#111] underline underline-offset-4 transition-colors hover:text-[#FF0000]"
              >
                {moreButtonLabel}
              </button>
            </div>
          ) : null}
        </div>
        {errorMessage ? (
          <p className="mt-3 text-sm font-poppins text-[#FF0000]" role="status">
            {errorMessage}
          </p>
        ) : null}
      </div>
    </section>
  )
}
